"use client";

import { useEffect, useState } from "react";
import { css, cx } from "styled-system/css";
import { Trash2, Plus } from "lucide-react";
import { Modal } from "@/components/ui/modal";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";

export type ProgramSessionRow = {
  id: string;
  serviceId: string;
  serviceName: string;
  quantity: number;
};

export type ProgramRow = {
  id: string;
  name: string;
  description: string | null;
  durationMonths: number;
  archived: boolean;
  activeAssignmentCount: number;
  sessions: ProgramSessionRow[];
};

type ServiceOption = { id: string; name: string; isActive: boolean };

const label = css({ fontSize: "xs", color: "#64748b" });

const selectStyle = css({
  width: "100%",
  px: "3",
  py: "2",
  rounded: "md",
  borderWidth: "1px",
  borderColor: "border.DEFAULT",
  bg: "bg.DEFAULT",
  color: "fg.DEFAULT",
  fontSize: "sm",
});

type SessionDraft = { key: string; serviceId: string; quantity: string };

let draftKeySeq = 0;
function nextDraftKey(): string {
  draftKeySeq += 1;
  return `draft-${draftKeySeq}`;
}

export function ProgramFormModal({
  open,
  onClose,
  program,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  /** null = modo creación; con datos = modo edición, precargado. */
  program: ProgramRow | null;
  onSaved: () => void;
}) {
  const { refresh } = useAuth();
  const isEdit = Boolean(program);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [durationMonths, setDurationMonths] = useState("1");
  const [sessions, setSessions] = useState<SessionDraft[]>([]);
  const [services, setServices] = useState<ServiceOption[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setErr(null);
    setName(program?.name ?? "");
    setDescription(program?.description ?? "");
    setDurationMonths(String(program?.durationMonths ?? 1));
    setSessions(
      program && program.sessions.length > 0
        ? program.sessions.map((s) => ({ key: nextDraftKey(), serviceId: s.serviceId, quantity: String(s.quantity) }))
        : [{ key: nextDraftKey(), serviceId: "", quantity: "1" }],
    );
  }, [open, program]);

  useEffect(() => {
    if (!open || services != null) return;
    void (async () => {
      const r = await fetchWithSessionSync("/api/dashboard/services?includeInactive=1", { credentials: "include" }, refresh);
      if (!r.ok) {
        setServices([]);
        return;
      }
      setServices((await r.json()) as ServiceOption[]);
    })();
  }, [open, services, refresh]);

  function addSessionRow() {
    setSessions((prev) => [...prev, { key: nextDraftKey(), serviceId: "", quantity: "1" }]);
  }

  function removeSessionRow(key: string) {
    setSessions((prev) => (prev.length <= 1 ? prev : prev.filter((s) => s.key !== key)));
  }

  function updateSessionRow(key: string, patch: Partial<SessionDraft>) {
    setSessions((prev) => prev.map((s) => (s.key === key ? { ...s, ...patch } : s)));
  }

  async function submit() {
    setErr(null);
    const nameTrim = name.trim();
    if (nameTrim.length < 2) {
      setErr("El nombre debe tener al menos 2 caracteres.");
      return;
    }
    const months = Number.parseInt(durationMonths, 10);
    if (!Number.isFinite(months) || months < 1 || months > 60) {
      setErr("Duración inválida (entre 1 y 60 meses).");
      return;
    }
    if (sessions.some((s) => !s.serviceId)) {
      setErr("Elegí un servicio para cada fila de sesión.");
      return;
    }
    const seenServices = new Set<string>();
    for (const s of sessions) {
      if (seenServices.has(s.serviceId)) {
        setErr("No repitas el mismo servicio en dos filas: sumá la cantidad en una sola fila.");
        return;
      }
      seenServices.add(s.serviceId);
    }
    const parsedSessions: { serviceId: string; quantity: number }[] = [];
    for (const s of sessions) {
      const q = Number.parseInt(s.quantity, 10);
      if (!Number.isFinite(q) || q < 1) {
        setErr("La cantidad de sesiones debe ser al menos 1.");
        return;
      }
      parsedSessions.push({ serviceId: s.serviceId, quantity: q });
    }
    if (parsedSessions.length === 0) {
      setErr("Agregá al menos un tipo de sesión.");
      return;
    }

    setBusy(true);
    try {
      const payload = {
        name: nameTrim,
        description: description.trim() || null,
        durationMonths: months,
        sessions: parsedSessions,
      };
      const r = await fetchWithSessionSync(
        isEdit && program ? `/api/dashboard/programs/${program.id}` : "/api/dashboard/programs",
        {
          method: isEdit ? "PATCH" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
        refresh,
      );
      if (!r.ok) {
        const j = (await r.json().catch(() => ({}))) as { error?: string };
        setErr(j.error ?? "No se pudo guardar el programa.");
        return;
      }
      onSaved();
      onClose();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? "Editar programa" : "Nuevo programa"} size="lg">
      <div className={css({ display: "flex", flexDir: "column", gap: "4" })}>
        {err ? <p className={css({ fontSize: "sm", color: "#b91c1c" })}>{err}</p> : null}

        <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
          <span className={label}>Nombre</span>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej. Programa de 6 sesiones" />
        </label>

        <div className={css({ display: "grid", gap: "3", gridTemplateColumns: "1fr 1fr" })}>
          <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
            <span className={label}>Duración (meses)</span>
            <Input
              type="number"
              min={1}
              max={60}
              value={durationMonths}
              onChange={(e) => setDurationMonths(e.target.value)}
            />
          </label>
        </div>

        <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
          <span className={label}>Descripción (opcional)</span>
          <textarea
            className={css({
              width: "100%",
              px: "3",
              py: "2",
              rounded: "md",
              borderWidth: "1px",
              borderColor: "border.DEFAULT",
              bg: "bg.DEFAULT",
              color: "fg.DEFAULT",
              fontSize: "sm",
              minH: "16",
              resize: "vertical",
              _focus: { outline: "none", borderColor: "accent.DEFAULT", boxShadow: "0 0 0 1px token(colors.brand.500)" },
            })}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>

        <div
          className={css({
            pt: "3",
            borderTopWidth: "1px",
            borderColor: "#e2e8f0",
            display: "flex",
            flexDir: "column",
            gap: "3",
          })}
        >
          <span className={label}>Sesiones incluidas</span>

          {services == null ? (
            <p className={css({ fontSize: "sm", color: "#94a3b8" })}>Cargando servicios…</p>
          ) : services.length === 0 ? (
            <p className={css({ fontSize: "sm", color: "#94a3b8" })}>
              Todavía no cargaste servicios. Creá al menos uno en la sección Servicios antes de armar un programa.
            </p>
          ) : (
            <div className={css({ display: "flex", flexDir: "column", gap: "2" })}>
              {sessions.map((s) => (
                <div key={s.key} className={css({ display: "flex", gap: "2", alignItems: "center" })}>
                  <select
                    value={s.serviceId}
                    onChange={(e) => updateSessionRow(s.key, { serviceId: e.target.value })}
                    className={cx(selectStyle, css({ flex: "1" }))}
                  >
                    <option value="">Elegí un servicio…</option>
                    {services.map((svc) => (
                      <option key={svc.id} value={svc.id}>
                        {svc.name}
                        {!svc.isActive ? " (inactivo)" : ""}
                      </option>
                    ))}
                  </select>
                  <Input
                    type="number"
                    min={1}
                    step={1}
                    value={s.quantity}
                    onChange={(e) => updateSessionRow(s.key, { quantity: e.target.value })}
                    className={css({ w: "20" })}
                    aria-label="Cantidad de sesiones"
                  />
                  <button
                    type="button"
                    onClick={() => removeSessionRow(s.key)}
                    disabled={sessions.length <= 1}
                    aria-label="Quitar fila"
                    className={css({
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      w: "9",
                      h: "9",
                      rounded: "md",
                      border: "none",
                      bg: "transparent",
                      color: "#ef4444",
                      cursor: "pointer",
                      flexShrink: 0,
                      _disabled: { opacity: 0.3, cursor: "not-allowed" },
                      _hover: { bg: "#fef2f2" },
                    })}
                  >
                    <Trash2 size={15} aria-hidden />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addSessionRow}
                className={css({
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "1.5",
                  alignSelf: "flex-start",
                  fontSize: "sm",
                  fontWeight: "medium",
                  color: "token(colors.brand.600)",
                  bg: "transparent",
                  border: "none",
                  cursor: "pointer",
                  py: "1",
                  _hover: { opacity: 0.75 },
                })}
              >
                <Plus size={15} aria-hidden />
                Agregar tipo de sesión
              </button>
            </div>
          )}
        </div>

        <div className={css({ display: "flex", gap: "2", justifyContent: "flex-end", pt: "2" })}>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={() => void submit()} disabled={busy}>
            {busy ? "Guardando…" : isEdit ? "Guardar cambios" : "Crear programa"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
